/**
 * 20260919 - IPHONE DUO SIDE DOCK: the NATIVE-MEASURED safe area, handed to the page in its own URL.
 *
 * WHY THIS EXISTS AT ALL — the one thing the settle ladder in foldStateStore.ts cannot fix. In
 * WKWebView the numbers `env(safe-area-inset-*)` resolves against are computed in the UI process
 * and pushed to the web process over IPC. Nothing running before the first paint can know them, so
 * the FIRST PAINTED FRAME is necessarily laid out as if there were no reserved strip and the
 * correction lands a frame or two later. On the board that correction is not a nudge — the top bar
 * is UNMOUNTED when a strip is reported (cs:6126) — so the owner saw chrome swap on every view
 * load. The fix is to move the measurement to the only process that has it before we do: Unity
 * reads the window's `safeAreaInsets` in points and appends them to the page URL, which is already
 * parsed and complete at module-evaluation time.
 *
 * THIS IS NOT A NEW IDEA HERE. macOS has shipped a native-measured inset in the URL since the
 * unified title bar landed — `&titlebarInsetLeading=<points>`, read by theme/macTitlebarInset.ts,
 * built by PurchasingWebViewPopupPanel.GetTitleBarInsetQueryFragment() and its twin in
 * TimelineWebViewPanel. The safe-area fragment is built in the same places, by
 * TimelineWebViewPanel.GetSafeAreaQueryFragment(), and appended last.
 *
 * THE CONTRACT, as the native side states it. Exactly one of:
 *
 *     &safeAreaTop=<n>&safeAreaRight=<n>&safeAreaBottom=<n>&safeAreaLeft=<n>
 *       [&safeAreaVerticalBarEdge=leading|trailing] [&safeAreaSplitView=0|1]
 *       [&safeAreaBarHead=0|1&safeAreaBarFoot=0|1
 *         [&safeAreaBarHeadExtent=<n>&safeAreaBarFootExtent=<n>]]
 *
 * or nothing at all. (20260926 - the two extents are points in the same "0.###" format as the
 * insets, emitted only beside the pair; see UrlSafeAreaSeed.barHeadExtent.)
 *   • ALL FOUR OR NONE. Unity emits the empty string iff it has nothing to say; if it has anything
 *     — any non-zero inset, OR a `verticalBarEdge` trait that is not Unspecified — all four numbers
 *     are emitted, zeros included. So a missing `safeAreaRight` never has to be disambiguated: if
 *     one is present they all are.
 *   • 20260919 - AND THE EDGE CAN BE THE ONLY THING IT HAS TO SAY, which is why the emit rule above
 *     is no longer "any inset is non-zero". In Split View on the inner display the pane's insets are
 *     0/0/0/0 in BOTH pane positions (owner measured it twice, mirrored) while the window's
 *     `verticalBarEdge` trait still names an edge — it "reflects the system's preferred edge
 *     regardless of whether a vertical bar is currently visible" (UIVerticalBarEdge.h, 27.1 SDK).
 *     Four zeros plus an edge plus `safeAreaSplitView=1` is therefore a MEANINGFUL fragment and not
 *     a no-op: it is what tells the first frame to carve a rail (platform/foldState.ts,
 *     SYNTHESIZED_VERTICAL_BAR_WIDTH — all three terms, because the same header says the trait may
 *     name an edge in a full-screen session too). Under the old all-zero-means-silence rule that
 *     pose would emit nothing and the board's first painted frame would be the top-bar layout,
 *     swapping to the dock a frame later — the exact flash this file exists to remove.
 *   • THE TWO NEW PARAMS ARE OPTIONAL, THE FOUR NUMBERS ARE NOT. `safeAreaVerticalBarEdge` is
 *     omitted when the trait is `UIVerticalBarEdgeUnspecified` — "no vertical bar here", which is
 *     every non-Duo device and every pose without one — and `safeAreaSplitView` is omitted when the
 *     window is not one pane of an arrangement. Omission means "not reported", never "false by
 *     measurement"; a present one must be well-formed or the WHOLE fragment is ignored (below).
 *   • ABSENT MEANS "NO NATIVE MEASUREMENT", NOT "THE INSETS ARE ZERO". macOS, the Editor, a
 *     home-button device, a stale plugin and every route Unity does not put it on produce no
 *     fragment, and standalone/online review have no Unity at all. Absent therefore falls through
 *     to `env()` exactly as today — which is also why this file returns `null`, never a zero rect,
 *     when it cannot answer. (A notched iPhone in any orientation and a Face ID iPad DO produce
 *     one — top/bottom non-zero, sides zero — which resolves to "no strip" exactly as env() will.)
 *   • ONLY ON ROUTES WHERE IT WILL AGREE WITH env(). Unity appends it solely where the WebView is
 *     pinned to the full window AND the route is one index.html turns the cover viewport on for
 *     (COVER_ROUTES) — the board, and projects — because the seed is held for at most a second
 *     after first paint and then env()
 *     governs. Anywhere else (a ReferenceRectTransform-driven view, a non-cover route) env() is
 *     zero by construction, and a seed would be a wrong number that snaps back. That rule lives on
 *     TimelineWebViewPanel.GetSafeAreaQueryFragment; nothing here can check it, so nothing here
 *     tries to.
 *   • UNITS are CSS px. Native reads UIKit points; a CSS px in this WebView IS a point (no page
 *     zoom, the WebView takes the screen scale), so the number drops into `${n}px` unconverted.
 *     There is no unit suffix in the param and there must not be one.
 *   • NUMBER FORMAT is C# `"0.###"` under InvariantCulture: up to three decimals, trailing zeros
 *     trimmed, `.` always the separator. `59f` → "59", `20.3333f` → "20.333", `0f` → "0".
 *   • Native clamps every value before formatting (non-finite → 0, negative → 0), so a well-formed
 *     fragment can never carry `NaN`, `Infinity` or a negative. We re-check anyway; see below.
 *   • Order is top/right/bottom/left (CSS order), but nothing here depends on it — URLSearchParams.
 *   • THE EDGE IS SPELLED IN WRITING-DIRECTION TERMS, `leading`/`trailing`, exactly as UIKit's enum
 *     and our own VerticalBarEdge do — never `left`/`right`. One vocabulary from the trait to the
 *     stylesheet's `inset-inline-*` means an RTL build needs no translation step anywhere.
 *   • `safeAreaSplitView` is `1` or `0`, never `true`/`false`: it is written by C# as an int.
 *   • 20260920 - THE OCCLUSION PAIR, `&safeAreaBarHead=0|1&safeAreaBarFoot=0|1`: is the system's own
 *     furniture at the head of the reserved strip, at the foot, at both, at neither? BOTH OR
 *     NEITHER, emitted only where the 27.1 reserved-region read actually answered AND an edge is
 *     known — the same rule the edge itself follows. Same int spelling as `safeAreaSplitView`, and
 *     for the same reason: C# writes these as literals, so no culture is in the path at all.
 *
 *     WHY THIS ONE IS IN THE SEED WHEN THE FILE HEADER USED TO SAY IT NEVER WOULD BE. The seed's
 *     other five params EXPIRE (foldStateStore.ts, "Retiring the URL seed"), and they expire for a
 *     good reason: `env()` has its own opinion about the four insets, and a boot snapshot must not
 *     outrank the page's own live reading of them. `env()` has NO opinion whatsoever about which
 *     END of the strip holds the camera — a circle inside a strip that is already outside the safe
 *     rect moves none of the four numbers (SideDock.module.css states this at length). So this pair
 *     is the one part of the fragment that nothing but a bridge push can ever supersede, and
 *     retiring it on the seed's clock throws away the only answer that page will ever have. The
 *     owner, 20260920, saw exactly that: the board rendering the per-end reserve while the Projects
 *     home beside it rendered the both-ends fallback, on the same device in the same pose.
 *     foldStateStore.ts is where the pair's longer lifetime — and the pose change that bounds it —
 *     is implemented.
 *
 * WHY THE PARSING IS PARANOID ANYWAY. This is a query parameter, and in standalone or online review
 * the URL is entirely attacker-controllable: anyone can send a link with `?safeAreaLeft=99999`. The
 * consequence of believing it is a shell that docks its controls into a strip that does not exist,
 * or pads a scrim by most of the viewport — a defacement, not a takeover, but free to prevent. So
 * the rule is ALL FOUR, each one a plain non-negative decimal within a sane bound, or the parameter
 * is ignored ENTIRELY and `env()` answers. Partial credit would be worse than nothing: three good
 * numbers and one hostile one still moves the chrome.
 *
 * THE SAME STRICTNESS, EXACTLY, FOR THE EDGE AND THE PANE FLAG. They are optional, so ABSENT is
 * fine — but a value that is not one of the spellings above is a malformed fragment and the whole
 * thing is ignored, numbers included. Not pedantry: that PAIR is the most dangerous thing in the
 * fragment, because since 20260919 an edge with a zero inset IN A PANE makes the page carve an 84px
 * rail for itself (foldState.ts). A hand-built
 * `?safeAreaVerticalBarEdge=trailing&safeAreaSplitView=1` on a link would shove a standalone
 * viewer's content 84px off the edge of their own window, so neither value gets the benefit of the
 * doubt — and note that the gate does NOT make the edge safer to guess at: it narrows what a
 * well-formed fragment can do, it does not excuse a sloppy one.
 *
 * 20260920 - THE OCCLUSION PAIR IS AS STRICT, AND FAILS DIFFERENTLY: a malformed one drops THE PAIR
 * AND ONLY THE PAIR, never the fragment. Both halves of that are deliberate.
 *   • AS STRICT: exactly the literal `0` or `1`, both present or neither. Anything else — `2`,
 *     `true`, empty, one without the other — is not a reading, and a value that is not a reading
 *     must not be believed as one.
 *   • BUT NOT FATAL, because the blast radius is a different order of magnitude from the edge's.
 *     The worst a hostile pair can do is move the dock's bottom group by the housing allowance at
 *     an end of a column that only exists because the edge — checked above — said so; it cannot
 *     conjure a rail, cannot inset the page, cannot touch a scrim. Meanwhile throwing away the four
 *     numbers and the edge over it would cost the FIRST PAINTED FRAME, which is the whole reason
 *     this file exists. Dropping just the pair lands the page on resolveHousingEnds()'s no-reading
 *     fallback — the both-ends geometry that shipped before these params existed — which is the
 *     correct answer to "nobody could say", and is what an absent pair means anyway.
 */

import type { SafeAreaInsets, VerticalBarEdge } from '@/platform/foldState.ts';

/**
 * The params, exactly as TimelineWebViewPanel.GetSafeAreaQueryFragment() spells them.
 *
 * 20260919 - AND THAT CLAIM IS NOW CHECKED, not asserted. It was false for a while: native appended
 * `&verticalBarEdge=` and `&splitView=1` while this table said `safeAreaVerticalBarEdge` /
 * `safeAreaSplitView`. Because the four NUMBERS matched, the fragment was accepted and the edge came
 * back null — a Split View pane silently got the no-dock layout, with both test suites green because
 * each asserted its own vocabulary. __tests__/nativeSafeAreaUrlContract.test.ts now reads the C# and
 * demands the names in THIS table, so the two ends can only drift together.
 */
export const SAFE_AREA_URL_PARAMS = {
  top: 'safeAreaTop',
  right: 'safeAreaRight',
  bottom: 'safeAreaBottom',
  left: 'safeAreaLeft',
  /** 20260919 - the native `verticalBarEdge` trait. Optional; omitted for Unspecified. */
  verticalBarEdge: 'safeAreaVerticalBarEdge',
  /** 20260919 - is this window one pane of an arrangement? Optional; omitted when it is not. */
  splitView: 'safeAreaSplitView',
  /** 20260920 - system furniture at the head of the strip. Optional, and never alone: the emitter
   *  writes this and `barFootOccluded` together or writes neither. */
  barHeadOccluded: 'safeAreaBarHead',
  /** 20260920 - system furniture at the foot. Rides with `barHeadOccluded`, always. */
  barFootOccluded: 'safeAreaBarFoot',
  /** 20260926 - how far the furniture at the head reaches down the strip, in points ("0.###").
   *  Emitted only beside the pair, and always with `barFootExtent`; 0 at an end measured clear. */
  barHeadExtent: 'safeAreaBarHeadExtent',
  /** 20260926 - the same, for the foot. Rides with `barHeadExtent`, always. */
  barFootExtent: 'safeAreaBarFootExtent',
} as const;

/**
 * 20260919 - what a well-formed fragment carries. More than four numbers since the Split View
 * measurement, because four numbers cannot express the pose it found: 0/0/0/0 WITH a vertical bar
 * edge.
 */
export interface UrlSafeAreaSeed {
  /** The four insets, as measured natively. */
  safeArea: SafeAreaInsets;
  /** The system's preferred vertical-bar edge, or null when native reported none (Unspecified, or
   *  a host that does not report the trait at all). NOT derived from the insets here — that
   *  inference belongs to whoever resolves the seed.
   *
   *  20260920 - AND THE RESOLVER NO LONGER CONSULTS IT ONLY WHEN THIS IS ABSENT, which is what
   *  that sentence used to end with. A measured asymmetric pair names the side and this answers
   *  only where four numbers cannot — the same precedence the bridge push uses, through the same
   *  function (platform/foldState.ts resolveVerticalBarEdge, which holds the defect behind it).
   *  Nothing changes for a well-formed fragment: native measures the trait and the four insets in
   *  the same breath, so they cannot disagree. */
  verticalBarEdge: VerticalBarEdge | null;
  /**
   * Whether native says this window is one pane of an arrangement.
   *
   * 20260919 - LOAD-BEARING, and this comment used to say the opposite. It said "RECORDED, NOT
   * ACTED ON", on the reasoning that an edge with no inset behind it was signal enough and that a
   * pane flag would be a mode test one step removed from a device test. The first half of that does
   * not survive the 27.1 header: `verticalBarEdge` names the system's preferred edge "regardless of
   * whether a vertical bar is currently visible" and goes Unspecified only where the CONTEXT never
   * places one — so "edge named, inset zero" is a shape a FULL-SCREEN inner-display session is
   * allowed to produce too, and carving an 84px rail out of that viewport is not something four
   * zeros gave us permission to do. The pane is the pose that was measured; it is therefore the
   * pose we act on, and this flag is the third term in the gate (foldState.ts,
   * resolveVerticalBarWidth).
   *
   * NOR IS IT A MODE TEST. Nothing selects a layout from it: it answers the factual question "has
   * the system given this window less than a display to itself?", which is what separates the
   * measured pose from the unmeasured one. Nothing downstream may branch on it for any other
   * purpose — the width still comes from resolveVerticalBarWidth, one answer for every consumer.
   *
   * `false` means "not reported as a pane", which is what every host that omits it also means, and
   * which resolves to no synthesized rail — the safe direction.
   */
  splitView: boolean;
  /**
   * 20260920 - WHICH END OF THE STRIP THE SYSTEM'S OWN FURNITURE OCCUPIES, as native measured it at
   * page-build time. `boolean | null` and never flattened, exactly as on FoldState: `false` is the
   * measurement "that end is clear", `null` is "the fragment did not say" — the 27.1 read could not
   * be taken, the host predates the params, or the pair arrived malformed and was dropped.
   *
   * ALWAYS BOTH OR NEITHER. The emitter writes them as a unit and this parser enforces it from its
   * side too, so a caller never has to decide what half a reading means.
   *
   * THE ONE FIELD OF THIS SEED THAT OUTLIVES THE SEED. See the file header: every other field here
   * expires because `env()` has its own live opinion about it, and nothing on the web has any
   * opinion at all about this one. foldStateStore.ts holds it past the seed's retirement and drops
   * it on a pose change.
   */
  barHeadOccluded: boolean | null;
  barFootOccluded: boolean | null;
  /**
   * 20260926 - HOW FAR the furniture reaches into the strip from each end, in CSS px — the depth
   * of the zone the pair names (170 at the head in closed portrait, 82 at the housing end in either
   * closed landscape). `null` unless the pair was kept AND both extents parsed; the parser drops
   * them together, and a bad extent never costs the pair (the pair then lands on the 96px
   * allowance, exactly as it did before the extents existed). Same lifetime as the pair: it
   * outlives the seed and ends on a pose change (foldStateStore.ts).
   */
  barHeadExtent: number | null;
  barFootExtent: number | null;
}

/**
 * The largest inset we will believe, in CSS px.
 *
 * Not a device constant and not a guess at hardware: it is the "this cannot be a system-reserved
 * region" line. The widest real reservation we know of is the Duo outer display's ~84pt strip, and
 * the tallest is a landscape iPhone's ~59pt side pair; 400 leaves an order of magnitude of headroom
 * for hardware nobody has shipped yet while still rejecting the values that would eat a phone
 * viewport whole. A number this large arriving from a real device would be a native bug, and
 * falling back to `env()` is the correct response to a native bug too.
 */
export const MAX_URL_SAFE_AREA_INSET = 400;

/**
 * Exactly the shape C#'s `"0.###"` can produce, and nothing else.
 *
 * Deliberately stricter than `Number.parseFloat`, which would take "59px" as 59, "1e9" as a
 * billion, and "  12  " as 12 — and stricter than `Number()`, which takes "" as 0, "0x40" as 64 and
 * "Infinity" as Infinity. A comma-decimal locale leaking through (the exact failure InvariantCulture
 * exists to prevent, "20,333") is rejected here rather than silently read as 20.
 */
const DECIMAL = /^\d+(?:\.\d+)?$/;

/**
 * 20260920 - one half of the occlusion pair, in the int spelling C# writes.
 *
 * Three outcomes and they are all distinct, which is why this cannot be a boolean-returning
 * function: `null` for absent ("the fragment did not say"), a boolean for `0`/`1`, and
 * `MALFORMED` for a present value that is neither — which the caller turns into a dropped PAIR,
 * not into a dropped fragment (see the header). Nothing here accepts `true`/`false`: those are not
 * a spelling native can produce, so seeing one means the URL was written by somebody else.
 */
const MALFORMED = Symbol('malformed');

function parseOcclusion(raw: string | null): boolean | null | typeof MALFORMED {
  if (raw === null) return null;
  if (raw === '0') return false;
  if (raw === '1') return true;
  return MALFORMED;
}

function parseInset(raw: string | null): number | null {
  if (raw === null) return null;
  if (!DECIMAL.test(raw)) return null;
  const value = Number(raw);
  // The regex already excludes NaN/Infinity/negatives; this is the bound, plus a belt-and-braces
  // finiteness check so a future loosening of the pattern cannot quietly admit one.
  if (!Number.isFinite(value) || value < 0 || value > MAX_URL_SAFE_AREA_INSET) return null;
  return value;
}

/**
 * 20260926 - one occlusion extent: the same "0.###" decimal and the same 0..400 bound as an inset
 * (MAX_OCCLUSION_EXTENT in foldState.ts is that number too). `null` for absent or malformed — the
 * caller drops BOTH extents on either, and keeps the pair.
 */
function parseExtent(raw: string | null): number | null {
  return parseInset(raw);
}

/**
 * The native measurement carried by this page's URL, or `null` when there isn't one.
 *
 * `null` covers all of: no params (every non-iOS host, and every iOS host with nothing to report),
 * a partial set, and anything malformed or out of range. The caller must treat `null` as "no seed
 * available" and fall through to `env()` — never as "the insets are zero".
 *
 * 20260920 - ONE FIELD IS EXEMPT from "malformed kills the fragment": the occlusion pair drops
 * itself and leaves the rest standing. The reasoning is in the header; the code is at the bottom of
 * this function.
 *
 * Pure and side-effect-free: it reads the string it is given, touches no DOM and no store, and
 * cannot throw. It is called at module-evaluation time, before the first render, which is the only
 * moment early enough to matter — an effect would run after the frame this exists to fix.
 */
export function parseUrlSafeArea(search: string): UrlSafeAreaSeed | null {
  try {
    const params = new URLSearchParams(search);

    const top = parseInset(params.get(SAFE_AREA_URL_PARAMS.top));
    const right = parseInset(params.get(SAFE_AREA_URL_PARAMS.right));
    const bottom = parseInset(params.get(SAFE_AREA_URL_PARAMS.bottom));
    const left = parseInset(params.get(SAFE_AREA_URL_PARAMS.left));

    // ALL FOUR OR NONE, enforced on the reading side as well as the writing side. A URL carrying
    // three of them is not something Unity can produce, so it is either a hand-edited link or a
    // truncation — and in both cases the missing side would have to be invented.
    if (top === null || right === null || bottom === null || left === null) return null;

    // 20260919 - THE TWO OPTIONAL PARAMS. Absent is a legitimate reading (Unspecified trait, a
    // window that is not a pane, a native build that predates them) and must not invalidate the
    // numbers. PRESENT-BUT-UNRECOGNISED is the opposite: it is either a hand-edited link or a
    // native format change, and in both cases believing the rest of the fragment while silently
    // dropping the field would be the wrong half to keep — the edge is precisely the field that
    // decides whether an 84px rail is carved out of the viewport.
    const rawEdge = params.get(SAFE_AREA_URL_PARAMS.verticalBarEdge);
    if (rawEdge !== null && rawEdge !== 'leading' && rawEdge !== 'trailing') return null;

    const rawSplit = params.get(SAFE_AREA_URL_PARAMS.splitView);
    if (rawSplit !== null && rawSplit !== '0' && rawSplit !== '1') return null;

    // 20260920 - THE OCCLUSION PAIR, AND THE ONE PLACE THIS PARSER FAILS SOFT. Both halves or
    // neither, each exactly `0` or `1`; anything else and the PAIR becomes "not reported" while the
    // four numbers and the edge above are still believed. The header argues why this one field is
    // allowed to fail differently from the edge — short version: a bad pair can only move the dock's
    // own bottom group inside a column the edge already authorised, whereas discarding the fragment
    // over it would cost the first painted frame, which is what this file is for.
    const head = parseOcclusion(params.get(SAFE_AREA_URL_PARAMS.barHeadOccluded));
    const foot = parseOcclusion(params.get(SAFE_AREA_URL_PARAMS.barFootOccluded));
    const pairKept = typeof head === 'boolean' && typeof foot === 'boolean';
    const occluded = pairKept
      ? { barHeadOccluded: head, barFootOccluded: foot }
      : { barHeadOccluded: null, barFootOccluded: null };

    // 20260926 - THE EXTENTS, and they fail softer still: both or neither, each a "0.###" decimal
    // in 0..400, and only beside a kept pair. Anything else drops THE EXTENTS and keeps the pair
    // (and everything above), so a bad extent costs at most the difference between `extent + 24`
    // and the 96px allowance at one end of a column the edge already authorised.
    const headExtent = parseExtent(params.get(SAFE_AREA_URL_PARAMS.barHeadExtent));
    const footExtent = parseExtent(params.get(SAFE_AREA_URL_PARAMS.barFootExtent));
    const extents = (pairKept && headExtent !== null && footExtent !== null)
      ? { barHeadExtent: headExtent, barFootExtent: footExtent }
      : { barHeadExtent: null, barFootExtent: null };

    return {
      safeArea: { top, right, bottom, left },
      verticalBarEdge: rawEdge,
      splitView: rawSplit === '1',
      ...occluded,
      ...extents,
    };
  } catch {
    // Parsing a URL must never be able to block boot — same rule as applyMacTitlebarInset().
    return null;
  }
}

/** The seed for THIS document, read once. Null in every host with no location (SSR, a bare jsdom). */
export function readUrlSafeArea(): UrlSafeAreaSeed | null {
  try {
    if (typeof window === 'undefined' || typeof window.location === 'undefined') return null;
    return parseUrlSafeArea(window.location.search);
  } catch {
    return null;
  }
}
